import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
  TDocumentFolder,
  TFolderContents,
  CreateFolderRequest,
  UpdateFolderRequest,
  MoveDocumentsRequest,
} from '@/types/document-folder';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

const ENDPOINT = 'document-folders';

// Query keys
export const FOLDER_KEYS = {
  all: ['folders'] as const,
  lists: () => [...FOLDER_KEYS.all, 'list'] as const,
  list: (params?: {
    property_location_id?: number;
    parent_folder_id?: number;
    include_system_folders?: boolean;
  }) => [...FOLDER_KEYS.lists(), params] as const,
  hierarchy: (folderId: number) => [...FOLDER_KEYS.all, 'hierarchy', folderId] as const,
  contents: (folderId: number, params?: { property_location_id?: number }) =>
    [...FOLDER_KEYS.all, 'contents', folderId, params] as const,
  rootContents: (params?: { property_location_id?: number }) =>
    [...FOLDER_KEYS.all, 'root-contents', params] as const,
};

// Hooks
export const useCreateFolder = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TDocumentFolder, Error, CreateFolderRequest>({
    mutationFn: async folderData => {
      try {
        const data = await handleApiCall<TDocumentFolder, CreateFolderRequest>(
          session,
          'POST',
          getResolvedEndpoint(ENDPOINT, {}),
          folderData
        );
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to create folder.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
    },
  });
};

export const useGetFolders = (
  params?: {
    property_location_id?: number;
    parent_folder_id?: number;
    include_system_folders?: boolean;
  },
  options?: Omit<UseQueryOptions<TDocumentFolder[]>, 'queryKey' | 'queryFn'>
) => {
  const { session } = useAuth();

  return useQuery<TDocumentFolder[]>({
    queryKey: FOLDER_KEYS.list(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<TDocumentFolder[]>(
          session,
          'GET',
          getResolvedEndpoint(ENDPOINT, params || {})
        );
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch folders.', { variant: 'error' });
        throw err;
      }
    },
    enabled: !!session?.access_token,
    ...options,
  });
};

export const useGetFolderHierarchy = (
  folderId: number,
  options?: Omit<UseQueryOptions<TDocumentFolder[]>, 'queryKey' | 'queryFn'>
) => {
  const { session } = useAuth();

  return useQuery<TDocumentFolder[]>({
    queryKey: FOLDER_KEYS.hierarchy(folderId),
    queryFn: async () => {
      try {
        const data = await handleApiCall<TDocumentFolder[]>(
          session,
          'GET',
          getResolvedEndpoint(`${ENDPOINT}/${folderId}/hierarchy`, {})
        );
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch folder hierarchy.', { variant: 'error' });
        throw err;
      }
    },
    enabled: !!folderId && !!session?.access_token,
    ...options,
  });
};

export const useGetFolderContents = (
  folderId: number,
  params?: { property_location_id?: number },
  options?: Omit<UseQueryOptions<TFolderContents>, 'queryKey' | 'queryFn'>
) => {
  const { session } = useAuth();

  return useQuery<TFolderContents>({
    queryKey: FOLDER_KEYS.contents(folderId, params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<TFolderContents>(
          session,
          'GET',
          `${ENDPOINT}/${folderId.toString()}/contents`,
          undefined,
          {
            params: {
              property_location_id: params?.property_location_id?.toString(),
            },
          }
        );
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch folder contents.', { variant: 'error' });
        throw err;
      }
    },
    enabled: !!folderId && !!session?.access_token,
    ...options,
  });
};

export const useGetRootContents = (
  params?: { property_location_id?: number },
  options?: Omit<UseQueryOptions<TFolderContents>, 'queryKey' | 'queryFn'>
) => {
  const { session } = useAuth();

  return useQuery<TFolderContents>({
    queryKey: FOLDER_KEYS.rootContents(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<TFolderContents, { property_location_id?: number }>(
          session,
          'GET',
          `${ENDPOINT}/root/contents`,
          params
        );
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch root contents.', { variant: 'error' });
        throw err;
      }
    },
    enabled: !!session?.access_token && !!params?.property_location_id,
    ...options,
  });
};

export const useUpdateFolder = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TDocumentFolder, Error, { id: number; data: UpdateFolderRequest }>({
    mutationFn: async ({ id, data }) => {
      try {
        const response = await handleApiCall<TDocumentFolder, UpdateFolderRequest>(
          session,
          'PUT',
          getResolvedEndpoint(`${ENDPOINT}/${id}`, {}),
          data
        );
        return response;
      } catch (err) {
        enqueueSnackbar('Failed to update folder.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
    },
  });
};

export const useDeleteFolder = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async folderId => {
      try {
        await handleApiCall(session, 'DELETE', getResolvedEndpoint(`${ENDPOINT}/${folderId}`, {}));
      } catch (err) {
        enqueueSnackbar('Failed to delete folder.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
    },
  });
};

export const useMoveDocumentsToFolder = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, MoveDocumentsRequest>({
    mutationFn: async moveData => {
      try {
        await handleApiCall<void, MoveDocumentsRequest>(
          session,
          'POST',
          getResolvedEndpoint(`${ENDPOINT}/move-documents`, {}),
          moveData
        );
      } catch (err) {
        enqueueSnackbar('Failed to move documents.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
    },
  });
};
